import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { View } from 'react-native';

import { Text, tokens } from '@fhs/ui';
import {
  DEFAULT_CONTENT,
  EMPTY_STATES_CONTENT,
} from '@fhs-legacy/frontend/src/components/store-locator-empty-states/constants';
import { EmptyStateCategories } from '@fhs-legacy/frontend/src/components/store-locator-empty-states/types';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { useGeolocation } from '@fhs-legacy/frontend/src/state/geolocation';
import { OrderLocatorTab } from '@fhs-legacy/frontend/src/utils/launchdarkly/variations';

import { useRequestLocationPermission } from '../../hooks/use-request-location-permission';
import { determineEmptyStateCategory } from '../../utils/determine-empty-state-category';

import { getEmptyStateIcon } from './empty-state.icons';
import { useEmptyStateMqStyles } from './empty-state.styles';
import { LocationServicesModal } from './location-services-modal';

/** The design draws every empty-state glyph as an 80px icon tinted to 10% black. */
const ICON_SIZE = 80;

/**
 * Copy from the design for a tab whose list is simply empty. The remaining categories
 * (signed out, errored) keep the shared translations.
 */
const NO_STORES_CONTENT: Partial<Record<OrderLocatorTab, { title: string; body: string }>> = {
  [OrderLocatorTab.NEARBY]: {
    title: 'No Nearby Locations',
    body: "We couldn't find any nearby restaurants. Try searching for a new location.",
  },
  [OrderLocatorTab.FAVORITE]: {
    title: 'No Favorite Locations',
    body: 'Save your favorite restaurants for faster ordering. Tap the heart on any restaurant to add it here.',
  },
  [OrderLocatorTab.RECENT]: {
    title: 'No Previous Locations',
    body: "Restaurants you've ordered from will appear here. Search for a location to get started.",
  },
};

interface StoreLocatorEmptyStateV2Props {
  tab: OrderLocatorTab;
  currentTabError: boolean;
}

/**
 * Panel-width empty state for the v2 locator. It reuses the shared category resolution and
 * icon set, but swaps in the copy from the design and renders "Enable location services" as an
 * inline link rather than a full-width button, which doesn't suit the narrow panel.
 */
export const StoreLocatorEmptyStateV2: FC<StoreLocatorEmptyStateV2Props> = ({
  tab,
  currentTabError,
}) => {
  const { formatMessage } = useIntl();
  const styles = useEmptyStateMqStyles();
  const { isAuthenticated } = useAuthContext();
  const { coordinatesAvailable } = useGeolocation();
  const { isDeniedModalOpen, closeDeniedModal, requestLocation, isPermissionGranted } =
    useRequestLocationPermission();

  const category = useMemo(
    () =>
      determineEmptyStateCategory({
        tab,
        isAuthenticated,
        locationPermissionsGranted: isPermissionGranted,
        currentTabError,
        coordinatesAvailable,
      }),
    [tab, isAuthenticated, isPermissionGranted, currentTabError, coordinatesAvailable]
  );

  const sharedContent = EMPTY_STATES_CONTENT[tab]?.[category] ?? DEFAULT_CONTENT;
  const Icon = getEmptyStateIcon(tab, category);

  const { title, body, showLocationLink } = useMemo(() => {
    if (category === EmptyStateCategories.NO_LOCATION) {
      return {
        title: 'Enable Location Services',
        body: undefined,
        showLocationLink: true,
      };
    }

    const noStoresContent = NO_STORES_CONTENT[tab];
    if (category === EmptyStateCategories.NO_STORES && noStoresContent) {
      return { ...noStoresContent, showLocationLink: false };
    }

    return {
      title: formatMessage({ id: sharedContent.titleId }),
      body: formatMessage({ id: sharedContent.bodyId }),
      showLocationLink: false,
    };
  }, [category, tab, sharedContent, formatMessage]);

  const summaryLabel = showLocationLink
    ? `${title}. Enable location services to see nearby restaurants, or search for a location instead.`
    : `${title}. ${body ?? ''}`;

  return (
    <View style={styles.wrapper} accessibilityLabel={summaryLabel}>
      <LocationServicesModal isOpen={isDeniedModalOpen} onClose={closeDeniedModal} />

      <Icon size={ICON_SIZE} color={tokens.colors.$blackOpacity10} />

      <Text.Heading type="two" style={styles.title}>
        {title}
      </Text.Heading>

      {showLocationLink ? (
        <Text.Paragraph size="md" style={styles.body}>
          <Text.Paragraph
            size="md"
            style={styles.link}
            accessibilityRole="link"
            onPress={requestLocation}
            testID="enable-location-services-link"
          >
            Enable location services
          </Text.Paragraph>
          {' to see nearby restaurants, or search for a location instead.'}
        </Text.Paragraph>
      ) : (
        Boolean(body) && (
          <Text.Paragraph size="md" style={styles.body}>
            {body}
          </Text.Paragraph>
        )
      )}
    </View>
  );
};
